// Dependencies
import React, { useEffect, useState } from 'react';

import DocumentCard from './DocumentCard';
import MessageCard from '../../UI/MessageCard';
import { useInfiniteScroll } from './useInfiniteScroll';
import RenderIfVisible from 'react-render-if-visible';

// Material UI Components
import { makeStyles } from '@material-ui/core/styles';
import { Grid, CircularProgress, Switch, FormControlLabel } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import useWindowSize from '../../../customHooks/useWindowSize';

// Tour
import { useTour } from '@reactour/tour';
import { tourSteps } from '../../TourSteps';
import { CONSTANTS } from '../../../constants/constants';
import { connect } from 'react-redux';

const ESTIMATED_ITEM_HEIGHT = 100;

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    backgroundColor: theme.palette.background.default,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 15,
      paddingRight: 15,
      marginTop: 20
    },
    [theme.breakpoints.up('xs')]: {
      marginTop: 20,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up(500)]: {
      marginTop: 40,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 40,
      paddingLeft: 12,
      paddingRight: 12
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: 40,
      paddingLeft: 33,
      paddingRight: 33
    },
    [theme.breakpoints.up('xl')]: {
      marginTop: 40,
      paddingLeft: 80,
      paddingRight: 80
    }
  },
  hideOnSmallScreen: {
    display: 'none',
    [theme.breakpoints.up('xl')]: {
      display: 'flex'
    }
  },
  gridItem: {
    width: '100%',
    marginBottom: 16
  },
  title: {
    color: '#CFD5E5',
    fontSize: '1rem',
    textTransform: 'uppercase',
    padding: '2rem 0'
  },
  topBtns: {
    padding: '1rem',
    textTransform: 'uppercase'
  },
  btn: {
    marginRight: '1rem'
  },
  search: {
    marginTop: '1.5rem'
  }
}));

const DocumentList = (props) => {
  const { documents, user } = props;
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const classes = useStyles();
  const [loading, noMoreData] = useInfiniteScroll(props);
  const location = useLocation();
  const { windowHeight } = useWindowSize();

  const path = location.pathname.split('/');
  const last = path.pop();
  const { t } = useTranslation();

  const [waitingForMe, setWaitingForMe] = useState(false);
  const [documentsToShow, setDocumentsToShow] = useState([]);

  useEffect(() => {
    if (last === 'document') {
      const tour = JSON.parse(localStorage.getItem('tour-document'));
      setSteps(tourSteps.createNewDocument);
      setCurrentStep(0);
      setIsOpen(tour);
    } else if (last === 'finished') {
      const tour = JSON.parse(localStorage.getItem('tour-finished'));
      setSteps(tourSteps.finished);
      setCurrentStep(0);
      setIsOpen(tour);
    }
  }, []);

  useEffect(() => {
    setDocumentsToShow(documents);
  }, [documents]);

  useEffect(() => {
    async function filterDocuments () {
      if (waitingForMe) {
        const filteredDocuments = await filterByWaitingForMe(documents);
        setDocumentsToShow(filteredDocuments);
      } else {
        setDocumentsToShow(documents);
      }
    }
    filterDocuments();
  }, [waitingForMe]);

  const filterByWaitingForMe = async (documents) => {
    if (!user || !user.attributes || !user.attributes.email) {
      return [];
    }

    return documents.filter((document) => {
      const flow = JSON.parse(document.flow);

      const stepToSign = flow.steps.some((step, index) => {
        if (step.status === CONSTANTS.STEP.STATUS.SEND_TO_SIGN && !step.automatic && flow.steps[index - 1].emails.includes(user.attributes.email)) {
          return true;
        }
      });

      return stepToSign;
    });
  };

  return (
    <>
      <Grid container spacing={3} className={classes.mainContainer} direction='row-reverse'>
        <Grid item xs={2}>
          {last === 'document' && (
            <FormControlLabel control={
              <Switch color='primary' onChange={() => setWaitingForMe(!waitingForMe)} />
            } label={t('documentListView:waitingForMe')} labelPlacement='top' />
          )}
        </Grid>
      </Grid>

      <Grid container className={classes.mainContainer} direction='column'>
        {documentsToShow && documentsToShow.length ? (
        <>
          {loading ? (
            <CircularProgress />
          ) : (
            <Grid container direction='row' item xs={12} md={10} >
              {documentsToShow.map((document, index) => {
                if (document === undefined) {
                  return null;
                } else {
                  return (
                    <Grid item key={document.id} xs={12} md={12}>
                      {(index <= 5) &&
                      <DocumentCard document={document} />
                      }
                      {(index > 5) &&
                      <RenderIfVisible defaultHeight={ESTIMATED_ITEM_HEIGHT} visibleOffset={windowHeight}>
                        <DocumentCard document={document} />
                      </RenderIfVisible>
                      }
                    </Grid>
                  );
                }
              })}
            </Grid>
          )}
          {noMoreData && (
            <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 8 }}>
              {t('documentListView:noMoreData')}
            </div>
          )}
        </>
        ) : loading ? (
          <CircularProgress />
        ) : (
          <MessageCard
            message={t('documentListView:documentDoesntExist')}
            textBtn={t('documentListView:createNewOne')}
            url='/document'
          />
        )}
      </Grid>
    </>
  );
};

DocumentList.propTypes = {
  // currentPath: PropTypes.string.isRequired,
  // documents: PropTypes.array.isRequired
  // location: PropTypes.object.isRequired,
  // role: PropTypes.string.isRequired
};

const mapStateToProps = (state) => {
  return {
    user: state.user
  };
};

export default connect(mapStateToProps, null)(DocumentList);
